import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { Alert, Button, Card, CardHeader, SkeletonRows, mutedTextClass } from '@/components/layout/ui'
import { ErrorBanner, cn } from '@/features/issue'
import { useWorkspace } from '@/lib/workspace'

import { useNotificationPreferences, useUpdateNotificationPreferences } from './hooks'
import type { NotificationPreference } from './types'

/**
 * `/{slug}/settings/notifications` — per-type in-app and email switches, and
 * the account-wide email switch above them.
 */
export default function NotificationSettingsPage() {
  const workspace = useWorkspace()
  const query = useNotificationPreferences(workspace.slug)
  const update = useUpdateNotificationPreferences(workspace.slug)

  const [emailEnabled, setEmailEnabled] = useState(true)
  const [rows, setRows] = useState<NotificationPreference[]>([])
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!query.data) return
    setEmailEnabled(query.data.email_enabled)
    setRows(query.data.data)
  }, [query.data])

  const dirty =
    query.data !== undefined &&
    (emailEnabled !== query.data.email_enabled ||
      rows.some((row) => {
        const original = query.data?.data.find((o) => o.type === row.type)
        return !original || original.in_app !== row.in_app || original.email !== row.email
      }))

  function toggle(type: string, field: 'in_app' | 'email') {
    setSaved(false)
    setRows((current) => current.map((row) => (row.type === type ? { ...row, [field]: !row[field] } : row)))
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    update.mutate(
      {
        email_enabled: emailEnabled,
        preferences: rows.map(({ type, in_app, email }) => ({ type, in_app, email })),
      },
      { onSuccess: () => setSaved(true) },
    )
  }

  return (
    <Card>
      <CardHeader
        title="Notifications"
        description="Choose what Taskly tells you about, in the app and by email. These settings are yours and apply in every workspace."
      />
      <form onSubmit={submit} className="space-y-5 p-5">
        {query.isPending ? (
          <SkeletonRows rows={5} />
        ) : query.isError ? (
          <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <>
            <label className="flex items-start gap-3 rounded-lg border border-ink-200 p-3 dark:border-ink-800">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(event) => {
                  setSaved(false)
                  setEmailEnabled(event.target.checked)
                }}
                className="mt-0.5 size-4 accent-brand-600"
              />
              <span>
                <span className="block text-sm font-medium">Email notifications</span>
                <span className={cn('block text-xs', mutedTextClass)}>
                  Turn this off to stop every notification email at once. In-app notifications are unaffected.
                </span>
              </span>
            </label>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={cn('text-left text-xs', mutedTextClass)}>
                    <th className="pb-2 font-medium">Event</th>
                    <th className="pb-2 text-center font-medium">In app</th>
                    <th className="pb-2 text-center font-medium">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100 dark:divide-ink-900">
                  {rows.map((row) => (
                    <tr key={row.type}>
                      <td className="py-2.5 pr-4">
                        <p className="font-medium">{row.label}</p>
                        <p className={cn('text-xs', mutedTextClass)}>{row.description}</p>
                      </td>
                      <td className="py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={row.in_app}
                          onChange={() => toggle(row.type, 'in_app')}
                          aria-label={`${row.label} in app`}
                          className="size-4 accent-brand-600"
                        />
                      </td>
                      <td className="py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={row.email}
                          disabled={!emailEnabled}
                          onChange={() => toggle(row.type, 'email')}
                          aria-label={`${row.label} by email`}
                          className="size-4 accent-brand-600 disabled:opacity-40"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {update.isError ? <ErrorBanner error={update.error} /> : null}
            {saved && !dirty ? <Alert tone="success">Notification settings saved.</Alert> : null}
            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={!dirty} loading={update.isPending}>
                Save notification settings
              </Button>
            </div>
          </>
        )}
      </form>
    </Card>
  )
}
